// A23 — Xodimlar va tashkilot. Module-local data layer (devApi/jitApi naqshi:
// `lib/adapters.ts` / `i18n/*` ga TEGILMAYDI — ular HOT). Ba'zi ekranlar JONLI
// backend ustida (roles / four-eyes / staff-activity), qolganlari greenfield →
// `safeList` bilan fail-soft bo'sh qaytadi (endpoint chiqqanda aynan mos keladi).
import { api } from '@/lib/api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'

/** Ro'yxatni qidiruv + sahifalash bilan FetchResult'ga aylantiradi. DataTable
 *  sahifasi 1-based (index.tsx `page: 1`) — shунга mos `(page-1)*size`. */
function toResult<T>(rows: T[], params: FetchParams, search?: (r: T) => string): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q && search ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = (params.page - 1) * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

/** Fail-soft ro'yxat: endpoint yo'q/xato bo'lsa bo'sh massiv (ekran yiqilmaydi). */
async function safeList<T>(path: string, key: string, signal: AbortSignal): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, { signal })
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

/* ── A23.05 Rollar (JONLI GET /admin/roles) ────────────────────── */
export interface RoleRow {
  role: string
  permissions: string[]
  permission_count: number
  all: boolean
  assignable: boolean
}
export const fetchRoles = (s: AbortSignal): Promise<RoleRow[]> =>
  safeList<RoleRow>('/admin/roles', 'roles', s)
/** POST /admin/roles — rol yaratish (greenfield; endpoint chiqqanda ishlaydi). */
export const createRole = (role: string, permissions: string[]): Promise<{ role: string }> =>
  api.post<{ role: string }>('/admin/roles', { role, permissions })

/* ── A23.06 Huquq berish so'rovlari (JONLI four-eyes navbati) ───── */
export interface AccessReqRow {
  id: number
  requester: string
  action: string
  target: string
  reason: string
  danger_level: number
  required_role: string
  status: string
  approver?: string | null
  created_at: number
  expires_at: number
  approved_at?: number | null
  execute_after?: number | null
}
export const fetchAccessRequests = (status: string, s: AbortSignal): Promise<AccessReqRow[]> =>
  api
    .get<{ items: AccessReqRow[] }>('/admin/four-eyes', {
      query: status ? { status } : {},
      signal: s,
    })
    .then((d) => d.items ?? [])
    .catch(() => [])
export const approveAccess = (id: number, note: string) =>
  api.post(`/admin/four-eyes/${id}/approve`, { approver_note: note })
export const rejectAccess = (id: number, note: string) =>
  api.post(`/admin/four-eyes/${id}/reject`, { approver_note: note })
export const executeAccess = (id: number) => api.post(`/admin/four-eyes/${id}/execute`, {})
export const cancelAccess = (id: number, reason: string) =>
  api.post(`/admin/four-eyes/${id}/cancel`, { reason })

/* ── A23.15 Xodim faoliyati auditi (JONLI GET /admin/staff-activity) ── */
export interface StaffActivityRow {
  admin: string
  actions: number
  last_action: string
  last_at: number
  last_ip?: string | null
}
export function fetchStaffActivity(params: FetchParams): Promise<FetchResult<StaffActivityRow>> {
  return safeList<StaffActivityRow>('/admin/staff-activity', 'items', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.admin} ${r.last_action}`),
  )
}

/* ── A23.09 Navbatga tayinlash qoidalari (greenfield) ──────────── */
export interface AssignmentRuleRow {
  id: number
  name: string
  queue: string
  role: string
  strategy: string
  priority: number
  enabled: boolean
}
export function fetchAssignmentRules(params: FetchParams): Promise<FetchResult<AssignmentRuleRow>> {
  return safeList<AssignmentRuleRow>('/admin/staff/assignment-rules', 'rules', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.name} ${r.queue} ${r.role}`),
  )
}

/* ── A23.10 Ish vaqti va smenalar (greenfield) ─────────────────── */
export interface ShiftRow {
  id: number
  staff: string
  role: string
  day: string
  start: string
  end: string
  timezone: string
}
export function fetchShifts(params: FetchParams): Promise<FetchResult<ShiftRow>> {
  return safeList<ShiftRow>('/admin/staff/shifts', 'shifts', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.staff} ${r.day}`),
  )
}

/* ── A23.11 Yuklama taqsimoti (greenfield) ─────────────────────── */
export interface WorkloadRow {
  id: number
  staff: string
  role: string
  open_tickets: number
  capacity: number
  utilization: number
}
export function fetchWorkload(params: FetchParams): Promise<FetchResult<WorkloadRow>> {
  return safeList<WorkloadRow>('/admin/staff/workload', 'items', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.staff} ${r.role}`),
  )
}

/* ── A23.12 Samaradorlik ko'rsatkichlari (greenfield) ──────────── */
export interface KpiRow {
  id: number
  staff: string
  role: string
  handled: number
  avg_response_sec: number
  csat: number
  sla_pct: number
}
export function fetchKpi(params: FetchParams): Promise<FetchResult<KpiRow>> {
  return safeList<KpiRow>('/admin/staff/kpi', 'items', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.staff} ${r.role}`),
  )
}

/* ── A23.13 Onboarding checklist (greenfield) ──────────────────── */
export interface OnboardingRow {
  id: number
  staff: string
  step: string
  status: string
  due_at: string | null
}
export function fetchOnboarding(params: FetchParams): Promise<FetchResult<OnboardingRow>> {
  return safeList<OnboardingRow>('/admin/staff/onboarding', 'items', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.staff} ${r.step}`),
  )
}

/* ── A23.14 Offboarding (greenfield) ───────────────────────────── */
export interface OffboardingRow {
  id: number
  staff: string
  step: string
  status: string
  revoked_at: string | null
}
export function fetchOffboarding(params: FetchParams): Promise<FetchResult<OffboardingRow>> {
  return safeList<OffboardingRow>('/admin/staff/offboarding', 'items', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.staff} ${r.step}`),
  )
}

/* ── A23.16 Ichki e'lonlar — JONLI GET /admin/announcements ─────── */
// Alohida greenfield `/admin/staff/announcements` YO'Q edi; ekran JONLI e'lon
// modulига (A15.19/20) qayta yo'naltirildi. Server qatorlari to'g'ridan-to'g'ri
// keladi: {id,title,severity,mandatory,enabled,live,starts_at,ends_at}.
export interface AnnouncementRow {
  id: number
  title: string
  severity: string
  mandatory: boolean
  live: boolean
  starts_at: string | null
}
export function fetchAnnouncements(params: FetchParams): Promise<FetchResult<AnnouncementRow>> {
  return safeList<AnnouncementRow>('/admin/announcements', 'announcements', params.signal).then(
    (rows) => toResult(rows, params, (r) => `${r.title} ${r.severity}`),
  )
}

/* ── A23.17 O'quv materiallari (greenfield) ────────────────────── */
export interface TrainingRow {
  id: number
  title: string
  kind: string
  required: boolean
  completions: number
  updated_at: string
}
export function fetchTraining(params: FetchParams): Promise<FetchResult<TrainingRow>> {
  return safeList<TrainingRow>('/admin/staff/training', 'items', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.title} ${r.kind}`),
  )
}

/* ── A23.18 Xodim sozlamalari (greenfield) ─────────────────────── */
export interface StaffSettingRow {
  id: number
  key: string
  value: string
  scope: string
  updated_at: string | null
}
export function fetchStaffSettings(params: FetchParams): Promise<FetchResult<StaffSettingRow>> {
  return safeList<StaffSettingRow>('/admin/staff/settings', 'items', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.key} ${r.scope}`),
  )
}

/** Module-local UZ strings (i18n HOT → key'ga keyin ko'chiriladi). */
export const ORG_STR = {
  // A23.05
  roleEditor: 'Rol yaratish/tahrirlash',
  roleEditorDesc: 'Rol katalogi (jonli /admin/roles) + yangi rol qoralamasi',
  role: 'Rol',
  permissions: 'Huquqlar',
  permCount: 'Huquqlar soni',
  assignable: 'Tayinlanadigan',
  allPerms: 'Barcha huquqlar',
  newRole: 'Yangi rol',
  roleName: 'Rol nomi',
  create: 'Yaratish',
  created: 'Rol yaratildi',
  createHint: 'Vergul bilan ajratilgan huquqlar (masalan: users.list.view, staff.member.view)',
  // A23.06
  accessReq: "Huquq berish so'rovlari",
  accessReqDesc: 'Ikki-nazorat (four-eyes) tasdiqlash navbati — xavfli amallar',
  requester: "So'rovchi",
  action: 'Amal',
  target: 'Obyekt',
  reason: 'Sabab',
  level: 'Daraja',
  approver: 'Tasdiqlovchi',
  expires: 'Muddati',
  approve: 'Tasdiqlash',
  reject: 'Rad etish',
  execute: 'Bajarish',
  cancel: 'Bekor qilish',
  note: 'Izoh',
  empty: "So'rovlar yo'q",
  all: 'Barchasi',
  pending: 'Kutilmoqda',
  approved: 'Tasdiqlangan',
  rejected: 'Rad etilgan',
  cancelled: 'Bekor qilingan',
  executed: 'Bajarilgan',
  expired: 'Muddati o‘tgan',
  fourEyesSelf: "O'z so'rovingizni tasdiqlay olmaysiz",
  done: 'Bajarildi',
  failed: 'Xatolik',
  // A23.09
  assignmentRules: 'Navbatga tayinlash qoidalari',
  ruleName: 'Qoida',
  queue: 'Navbat',
  strategy: 'Strategiya',
  priority: 'Ustuvorlik',
  enabled: 'Yoqilgan',
  // A23.10
  shifts: 'Ish vaqti va smenalar',
  staff: 'Xodim',
  day: 'Kun',
  start: 'Boshlanishi',
  end: 'Tugashi',
  timezone: 'Vaqt zonasi',
  // A23.11
  workload: 'Yuklama taqsimoti',
  openTickets: 'Ochiq murojaatlar',
  capacity: 'Sig‘im',
  utilization: 'Bandlik %',
  // A23.12
  kpi: "Samaradorlik ko'rsatkichlari",
  handled: 'Ishlangan',
  avgResponse: "O'rtacha javob (s)",
  csat: 'CSAT',
  slaPct: 'SLA %',
  // A23.13
  onboarding: 'Onboarding checklist',
  step: 'Qadam',
  status: 'Holat',
  dueAt: 'Muddat',
  // A23.14
  offboarding: 'Offboarding',
  revokedAt: 'Olib tashlangan',
  // A23.15
  activity: 'Xodim faoliyati auditi',
  activityDesc: 'Har admin bo‘yicha amallar soni + oxirgi amal (jonli agregat)',
  actions: 'Amallar',
  lastAction: 'Oxirgi amal',
  lastAt: 'Oxirgi vaqt',
  lastIp: 'IP',
  // A23.16 (JONLI /admin/announcements)
  announcements: "Ichki e'lonlar",
  title: 'Sarlavha',
  severity: 'Muhimlik',
  mandatory: 'Majburiy',
  live: 'Faol',
  startsAt: 'Boshlanish',
  // A23.17
  training: "O'quv materiallari",
  kind: 'Turi',
  required: 'Majburiy',
  completions: 'Yakunlaganlar',
  updatedAt: 'Yangilangan',
  // A23.18
  settings: 'Xodim sozlamalari',
  key: 'Kalit',
  value: 'Qiymat',
  scope: 'Qamrov',
} as const

export const REQ_STATUS: Record<string, { label: string; pill: string }> = {
  pending: { label: ORG_STR.pending, pill: 'bg-warning/10 text-warning' },
  approved: { label: ORG_STR.approved, pill: 'bg-success/10 text-success' },
  rejected: { label: ORG_STR.rejected, pill: 'bg-danger/10 text-danger' },
  cancelled: { label: ORG_STR.cancelled, pill: 'bg-surface-3 text-tertiary' },
  executed: { label: ORG_STR.executed, pill: 'bg-accent/10 text-accent' },
  expired: { label: ORG_STR.expired, pill: 'bg-surface-3 text-tertiary' },
}
